/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/templates/QaTemplateAddProduct.tsx
 * FILE_ID: QA-TEMPLATES-ADD-PRODUCT-001
 * PURPOSE: One Add product control for the focused location, checklist, or step.
 */
import { useEffect, useRef, useState } from 'react'
import { QaProductSearchField } from '../../../qa/QaProductSearchField'
import {
  appendProductName,
  removeProductName,
  replaceProductName,
} from '../../../qa/qaProductResolve'
import type { JobHubHttpClient } from '../../../jobHubDataAdapter'
import {
  namesForFocus,
  writeFocusedNames,
  type QaTemplateProductDraft,
  type QaTemplateProductFocus,
} from './qaTemplateProductDraft'

export function QaTemplateAddProduct({
  client,
  focus,
  draft,
  locationType,
  onDraft,
  layout,
}: {
  client?: JobHubHttpClient
  focus: QaTemplateProductFocus | null
  draft: QaTemplateProductDraft
  locationType: string | null
  onDraft: (next: QaTemplateProductDraft) => void
  layout: 'popover' | 'inline'
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const names = focus ? namesForFocus(draft, focus, locationType) : []

  useEffect(() => {
    if (!open || layout !== 'popover') return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('mousedown', onPointer)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onPointer)
    }
  }, [layout, open])

  const write = (mutate: (current: string[]) => string[]) => {
    if (!focus) return
    const current = namesForFocus(draft, focus, locationType)
    onDraft(writeFocusedNames(draft, focus, mutate(current)))
  }

  const panel = !focus ? (
    <p className="text-sm text-muted-foreground">Select a location, checklist, or step.</p>
  ) : (
    <div className="space-y-2">
      <QaProductSearchField
        value=""
        client={client}
        ariaLabel="Add product"
        placeholder="Search or type a product"
        onCommit={(name) => write((current) => appendProductName(current, name))}
      />
      {names.length === 0 ? (
        <p className="text-xs text-muted-foreground">No products on this selection yet.</p>
      ) : (
        <ul className="space-y-1">
          {names.map((name) => (
            <li key={name} className="flex min-w-0 items-center gap-1">
              <input
                defaultValue={name}
                aria-label={`Edit ${name}`}
                onBlur={(event) => {
                  const next = event.target.value
                  if (next.trim() === name) return
                  write((current) => replaceProductName(current, name, next))
                }}
                className="min-w-0 flex-1 rounded-md border border-border px-2 py-1 text-sm"
              />
              <button
                type="button"
                aria-label={`Remove ${name}`}
                onClick={() => write((current) => removeProductName(current, name))}
                className="shrink-0 px-2 text-xs text-muted-foreground"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )

  const button = (
    <button
      type="button"
      data-testid="qa-template-add-product"
      aria-expanded={open}
      onClick={() => setOpen((current) => !current)}
      className="inline-flex min-h-[32px] items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs font-medium"
    >
      Add product
    </button>
  )
  if (layout === 'inline') {
    return (
      <>
        {button}
        {open ? <div className="basis-full max-h-[45vh] overflow-y-auto py-2">{panel}</div> : null}
      </>
    )
  }
  return (
    <div className="relative" ref={rootRef}>
      {button}
      {open ? (
        <div className="absolute left-0 top-full z-40 mt-1 w-72 max-h-[45vh] overflow-y-auto rounded-md border border-border bg-card p-2 shadow-lg">
          {panel}
        </div>
      ) : null}
    </div>
  )
}
